import React, { useCallback, useMemo, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ClipboardText, DownloadSimple, QrCode } from "@phosphor-icons/react";
import {
  TextInput,
  Button,
  Skeleton,
  Stack,
  Group,
  Text,
  Tooltip,
} from "@mantine/core";
import { useInputState, useDebouncedValue } from "@mantine/hooks";

export default function QrPage() {
  const [stringValue, setStringValue] = useInputState("");
  const [debounced] = useDebouncedValue(stringValue, 200);
  const svgRef = useRef<HTMLDivElement | null>(null);
  const qrValue = useMemo(() => debounced.trim(), [debounced]);
  const hasQrValue = qrValue.length > 0;

  const downloadSVG = useCallback((): void => {
    const ref = svgRef.current;
    if (!ref) return;
    const svg = ref.querySelector("svg");
    if (!svg) return;
    const serializedSVG = new XMLSerializer().serializeToString(svg);
    const anchor = document.createElement("a");
    anchor.href = "data:image/svg+xml;base64," + btoa(serializedSVG);
    anchor.download = "qrcode.svg";
    anchor.click();
  }, []);

  const pasteFromClipboard = useCallback(async (): Promise<void> => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setStringValue(clipboardText);
    } catch (error) {
      console.error("Panodan metin okunamadı:", error);
    }
  }, [setStringValue]);

  return (
    <div className="tool-page">
      <div className="tool-glass-panel">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (hasQrValue) downloadSVG();
          }}
        >
          <Stack gap="lg">
            <div className="tool-page-title">
              <QrCode size={28} weight="duotone" />
              <Text fw={600} size="lg">Vektörel QR Oluşturucu</Text>
            </div>

            <TextInput
              value={stringValue}
              onChange={setStringValue}
              size="md"
              label="QR İçeriği"
              placeholder="Örn: https://ornek.com"
              autoFocus
              rightSectionPointerEvents="all"
              rightSection={
                <Tooltip label="Panodan Yapıştır" withArrow>
                  <Button
                    type="button"
                    variant="light"
                    size="compact-sm"
                    onClick={pasteFromClipboard}
                    aria-label="Panodan yapıştır"
                  >
                    <ClipboardText size={16} weight="bold" />
                  </Button>
                </Tooltip>
              }
            />

            <div className="qr-preview">
              {hasQrValue ? (
                <div ref={svgRef}>
                  <QRCodeSVG
                    value={qrValue}
                    size={256}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"L"}
                    includeMargin={false}
                  />
                </div>
              ) : (
                <Skeleton width={256} height={256} radius="md" />
              )}
            </div>

            <Group justify="flex-end">
              <Button type="submit" size="md" disabled={!hasQrValue}>
                <DownloadSimple size={16} style={{ marginRight: 6 }} />
                SVG İndir
              </Button>
            </Group>
          </Stack>
        </form>
      </div>
    </div>
  );
}
